import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import { useAuth } from '../../state/AuthContext.jsx';
import { FormField } from '../FormField.jsx';
import { TextInput } from '../TextInput.jsx';
import { Modal } from '../Modal.jsx';
import { formatDateDDMonYYYY } from '../../../utils/dates.js';

const CONFIRM_TOKEN = 'DELETE';

export function DangerZoneSection({ user, organization, navigate }) {
  // OWNER-RESTORE (v3.33): the platform-owner account is undeletable — show the
  // same sentence the server returns (403 owner_undeletable) in place of any
  // delete control, and skip the branch fetch entirely. All other accounts are
  // unaffected.
  const isOwner = !!user?.is_owner;
  const [serverState, setServerState] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (isOwner) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getSettingsDangerBranch();
        if (cancelled) return;
        setServerState(data);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err);
      }
    })();
    return () => { cancelled = true; };
  }, [isOwner]);

  if (isOwner) {
    return (
      <section className="settings-section settings-section-danger">
        <h2>Danger Zone</h2>
        <p className="danger-owner-note" role="note">The platform owner account cannot be deleted.</p>
      </section>
    );
  }

  if (loadError) {
    return (
      <section className="settings-section settings-section-danger">
        <h2>Danger Zone</h2>
        <p className="invite-error" role="alert">
          Couldn&rsquo;t load deletion options. Please refresh the page.
        </p>
      </section>
    );
  }

  if (!serverState) {
    return (
      <section className="settings-section settings-section-danger">
        <h2>Danger Zone</h2>
        <p className="form-loading">Loading…</p>
      </section>
    );
  }

  const branch = serverState.branch;

  return (
    <section className="settings-section settings-section-danger">
      <h2>Danger Zone</h2>
      {branch === 'a' && (
        <BranchATransferAndDelete
          serverState={serverState}
          user={user}
          organization={organization}
          navigate={navigate}
        />
      )}
      {branch === 'b' && (
        <BranchBDeleteWithGrace
          serverState={serverState}
          organization={organization}
          navigate={navigate}
        />
      )}
      {branch === 'c' && (
        <BranchCLeaveOrg
          serverState={serverState}
          navigate={navigate}
        />
      )}
      {branch === 'd' && (
        <BranchDDeleteExpired
          serverState={serverState}
          navigate={navigate}
        />
      )}
    </section>
  );
}

// ── Branch A: admin + has-other-members ────────────────────────────────────

function BranchATransferAndDelete({ serverState, navigate }) {
  const auth = useAuth();
  const [newAdminId, setNewAdminId] = useState(serverState.candidates?.[0]?.id || '');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const candidates = serverState.candidates || [];

  if (candidates.length === 0) {
    return (
      <p className="invite-error" role="alert">
        No eligible teammates available for admin transfer. Invite a member first
        or wait for an outstanding invitation to be accepted.
      </p>
    );
  }

  return (
    <>
      <p className="settings-section-hint">
        You&rsquo;re the only admin of <strong>{serverState.organization_name}</strong>.
        Promote a teammate to admin, then your account will be deleted.
      </p>
      <FormField label="Promote to admin" htmlFor="branch-a-new-admin">
        <select
          id="branch-a-new-admin"
          className="form-field-input"
          value={newAdminId}
          onChange={(e) => setNewAdminId(e.target.value)}
        >
          {candidates.map((c) => (
            <option key={c.id} value={c.id}>
              {c.full_name} ({c.email})
            </option>
          ))}
        </select>
      </FormField>
      <div className="settings-form-actions">
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => setConfirmOpen(true)}
          disabled={!newAdminId}
        >
          Transfer and delete my account
        </button>
      </div>
      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Transfer admin and delete account"
        body={
          <>
            <p>
              <strong>{candidatesById(candidates, newAdminId)?.full_name}</strong> will
              become the admin of <strong>{serverState.organization_name}</strong>.
            </p>
            <p>Your account will be deleted immediately. This cannot be undone.</p>
          </>
        }
        confirmLabel="Transfer and delete"
        onConfirm={async () => {
          await api.postDangerBranchA({
            new_admin_user_id: newAdminId,
            confirm: CONFIRM_TOKEN,
          });
          await auth.logout();
          navigate('/login', 'account=deleted');
        }}
      />
    </>
  );
}

// ── Branch B: admin + sole user ────────────────────────────────────────────

function BranchBDeleteWithGrace({ serverState, navigate }) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <p className="settings-section-hint">
        Delete your account for <strong>{serverState.organization_name}</strong>.
      </p>
      <p className="settings-section-hint">
        Your organisation data is preserved until{' '}
        <strong>{formatDate(serverState.scheduled_deletion_at)}</strong>
        {serverState.subscription_expires_at
          ? null
          : ' (30 days from today, since your plan has no scheduled end date)'}
        , then permanently deleted. Your account is closed immediately.
      </p>
      <div className="settings-form-actions">
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => setConfirmOpen(true)}
        >
          Delete my account
        </button>
      </div>
      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Delete account"
        body={
          <>
            <p>
              Your account will be closed now. Organisation data is kept until{' '}
              <strong>{formatDate(serverState.scheduled_deletion_at)}</strong>{' '}
              and then deleted permanently.
            </p>
          </>
        }
        confirmLabel="Delete account"
        onConfirm={async () => {
          const data = await api.postDangerBranchB({ confirm: CONFIRM_TOKEN });
          if (data?.redirect && typeof window !== 'undefined') {
            window.location.href = data.redirect;
            return;
          }
          if (navigate) navigate('/login', `account=deleted&grace=true&until=${encodeURIComponent(data.scheduled_deletion_at)}`);
        }}
      />
    </>
  );
}

// ── Branch C: regular member self-removal ──────────────────────────────────

function BranchCLeaveOrg({ serverState, navigate }) {
  const auth = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <p className="settings-section-hint">
        Leave <strong>{serverState.organization_name}</strong> and delete your
        CritWise account. Your reports remain available to the organisation.
      </p>
      <div className="settings-form-actions">
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => setConfirmOpen(true)}
        >
          Leave and delete my account
        </button>
      </div>
      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Leave organisation and delete account"
        body={
          <>
            <p>
              You&rsquo;ll lose access to <strong>{serverState.organization_name}</strong>
              {' '}and your account will be deleted immediately. This cannot be undone.
            </p>
          </>
        }
        confirmLabel="Leave and delete"
        onConfirm={async () => {
          await api.postDangerBranchC({ confirm: CONFIRM_TOKEN });
          await auth.logout();
          navigate('/login', 'account=deleted');
        }}
      />
    </>
  );
}

// ── Branch D: expired admin self-delete ────────────────────────────────────

function BranchDDeleteExpired({ serverState, navigate }) {
  const auth = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const memberCount = serverState.member_count ?? 0;

  return (
    <>
      <p className="settings-section-hint">
        Your subscription has expired. Delete <strong>{serverState.organization_name}</strong>
        {' '}and all data permanently.
      </p>
      <p className="settings-section-hint">
        This will delete <strong>{memberCount} {memberCount === 1 ? 'account' : 'accounts'}</strong>
        {' '}and every report owned by the organisation.
      </p>
      <div className="settings-form-actions">
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => setConfirmOpen(true)}
        >
          Delete organisation and all data
        </button>
      </div>
      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Delete everything"
        body={
          <>
            <p>
              <strong>{serverState.organization_name}</strong>, {memberCount}{' '}
              {memberCount === 1 ? 'account' : 'accounts'}, and every report will be
              deleted immediately.
            </p>
            <p>This cannot be undone.</p>
          </>
        }
        confirmLabel="Delete everything"
        onConfirm={async () => {
          await api.postDangerBranchD({ confirm: CONFIRM_TOKEN });
          await auth.logout();
          navigate('/login', 'account=deleted');
        }}
      />
    </>
  );
}

// ── Shared confirm modal: type "DELETE" to enable confirm ──────────────────

function ConfirmDeleteModal({ open, onClose, title, body, confirmLabel, onConfirm }) {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setText('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const canConfirm = text === CONFIRM_TOKEN && !submitting;

  const handleConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm();
    } catch (err) {
      setError(err);
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!submitting) onClose(); }}
      title={title}
      labelledBy="danger-confirm-title"
      describedBy="danger-confirm-body"
    >
      <div id="danger-confirm-body" className="modal-confirm-body">{body}</div>
      <FormField
        label={`Type ${CONFIRM_TOKEN} to confirm`}
        htmlFor="danger-confirm-input"
      >
        <TextInput
          id="danger-confirm-input"
          name="confirm"
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          aria-label={`Type ${CONFIRM_TOKEN} to confirm`}
        />
      </FormField>
      {error && (
        <p className="invite-error" role="alert">
          {error instanceof ApiError
            ? (error.message || 'Couldn\u2019t complete that action.')
            : (error.message || 'Couldn\u2019t complete that action.')}
        </p>
      )}
      <div className="modal-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-danger"
          onClick={handleConfirm}
          disabled={!canConfirm}
        >
          {submitting ? 'Please wait…' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

function candidatesById(candidates, id) {
  return candidates.find((c) => c.id === id) || null;
}

// A1 (D-ListDateFormat): route through the one shared DD Mon YYYY formatter
// (was locale-dependent month:'long' — now "05 Aug 2026" like every surface).
function formatDate(iso) {
  return formatDateDDMonYYYY(iso);
}
